import { 
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  Inject,
  PLATFORM_ID,
  AfterViewInit,
  OnDestroy
} from '@angular/core';

import { isPlatformBrowser } from '@angular/common';
import flatpickr from 'flatpickr';
import { LabelComponent } from '../label/lable';

@Component({
  selector: 'app-date-picker',
  imports: [LabelComponent],
  templateUrl: './date-picker.html'
})
export class DatePicker implements AfterViewInit, OnDestroy {

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  @Input() id!: string;
  @Input() mode: 'single' | 'multiple' | 'range' | 'time' = 'single';
  @Input() defaultDate?: string | Date | string[] | Date[];
  @Input() label?: string;
  @Input() placeholder?: string;

  @Output() dateChange = new EventEmitter<any>();

  @ViewChild('dateInput', { static: false })
  dateInput!: ElementRef<HTMLInputElement>;

  private flatpickrInstance: flatpickr.Instance | undefined;
  private repositionRafId: number | null = null;

  ngAfterViewInit() {

    // 🚀 Only run in browser (not SSR)
    if (isPlatformBrowser(this.platformId)) {

      this.flatpickrInstance = flatpickr(this.dateInput.nativeElement, {
        mode: this.mode,
        static: false,
        monthSelectorType: 'static',
        dateFormat: 'Y-m-d',
        defaultDate: this.defaultDate,
        onChange: (selectedDates, dateStr, instance) => {
          this.dateChange.emit({ selectedDates, dateStr, instance });
        },
        onOpen: () => {
          // Anchor the calendar with `position: fixed`, recomputed every
          // frame while it's open. flatpickr's own `absolute` positioning
          // gets confused when the input sits inside a `position: sticky`
          // ancestor (our filter sidebar), so it drifts away from the input
          // as soon as the page scrolls — a one-off scroll listener isn't
          // enough since flatpickr keeps re-asserting its own (wrong)
          // position afterwards, so we keep overriding it every frame.
          this.startRepositionLoop();
        },
        onClose: () => {
          this.stopRepositionLoop();
        },
      });

    }
  }

  /**
   * Clears the selection and the visible input.
   *
   * flatpickr owns the input's text, so a parent resetting its own filter state
   * (a "clear all" button, removing a filter chip) leaves the stale date sitting
   * in the box — the parent has to reach in here to clear it.
   */
  clear(): void {
    // `false` — don't re-emit dateChange. Callers clear this as part of resetting
    // their own filter state and reload themselves; emitting would run that twice.
    this.flatpickrInstance?.clear(false);
    if (this.dateInput?.nativeElement) this.dateInput.nativeElement.value = '';
  }

  private startRepositionLoop(): void {
    const loop = () => {
      this.repositionCalendar();
      this.repositionRafId = requestAnimationFrame(loop);
    };
    this.repositionRafId = requestAnimationFrame(loop);
  }

  private stopRepositionLoop(): void {
    if (this.repositionRafId !== null) {
      cancelAnimationFrame(this.repositionRafId);
      this.repositionRafId = null;
    }
  }

  private repositionCalendar(): void {
    const calendar = this.flatpickrInstance?.calendarContainer;
    if (!calendar || !this.dateInput?.nativeElement) return;
    const rect = this.dateInput.nativeElement.getBoundingClientRect();
    calendar.style.position = 'fixed';
    calendar.style.top = `${rect.bottom + 4}px`;
    calendar.style.left = `${rect.left}px`;
  }

  ngOnDestroy() {
    this.stopRepositionLoop();

    // prevent destroy error
    if (this.flatpickrInstance && typeof this.flatpickrInstance.destroy === 'function') {
      this.flatpickrInstance.destroy();
    }

  }
}
